/**
 * English dictionary — the SINGLE SOURCE OF TRUTH for translation keys.
 * Its shape defines the `Dictionary` type; every other locale must match it
 * exactly (enforced by TypeScript). Never hardcode UI strings in components.
 */
export const en = {
  common: {
    appName: 'Astral Studio',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    duplicate: 'Duplicate',
    search: 'Search',
    loading: 'Loading…',
    back: 'Back',
    confirm: 'Confirm',
    close: 'Close',
    retry: 'Retry',
    comingSoon: 'Coming soon',
    optional: 'optional',
    add: 'Add',
    remove: 'Remove',
    done: 'Done',
    archive: 'Archive',
    restore: 'Restore',
    copied: 'Copied',
  },
  language: {
    title: 'Choose your language',
    subtitle: 'You can change this later in Settings.',
    continue: 'Continue',
  },
  onboarding: {
    welcomeSubtitle: 'Sticker Studio',
    enterSub: 'Craft Telegram sticker packs',
    touchStatue: 'Touch the statue to enter',
    // Membership is not verified anywhere in the app, so this invites — it must
    // never imply joining is required.
    channelPrompt: 'Join to use Sticker Studio',
    chooseLook: 'Personalize your studio',
    colorTheme: 'Color theme',
    resetTheme: 'Reset to Astral default',
    appearance: 'Appearance',
  },
  nav: {
    home: 'Home',
    projects: 'Projects',
    channels: 'Channels',
    settings: 'Settings',
    admin: 'Admin',
  },

  guide: {
    title: 'Guide',
    subtitle: 'Everything you need to master Astral Sticker Studio.',
    searchPlaceholder: 'Search the guide…',
    noResults: 'Nothing found.',
    start: {
      title: 'Getting started',
      whatIsT: 'What is Astral?',
      whatIsD: 'Astral Sticker Studio turns your images and videos into polished Telegram sticker packs — created, published and updated entirely from this Mini App.',
      createT: 'Create a project',
      createD: 'Tap “Create New Pack”, give it a title and choose Image, Video or Mixed. Each project becomes one Telegram sticker pack.',
      uploadT: 'Add stickers',
      uploadD: 'Upload one or many files at once. Astral converts each to Telegram’s required format automatically — no manual resizing.',
    },
    stickers: {
      title: 'Stickers',
      processT: 'Automatic conversion',
      processD: 'Images become 512×512 WebP stickers; videos become short Telegram-compatible WebM clips. Transparency is preserved.',
      reorderT: 'Reorder',
      reorderD: 'Press and hold a sticker, then drag it to a new position. The order you see is the order Telegram will use.',
      replaceT: 'Replace or delete',
      replaceD: 'Tap a sticker to preview it, then replace its media or remove it. On a published pack these changes apply on your next update.',
    },
    emoji: {
      title: 'Emoji',
      singleT: 'One or many emoji',
      singleD: 'Every sticker needs at least one emoji. Tap a sticker to change it, or add several (up to Telegram’s limit) for richer search.',
      deviceT: 'Any device emoji',
      deviceD: 'Use the built-in picker or your phone’s own emoji keyboard — flags, skin tones, ZWJ and keycap emoji all work.',
      presetsT: 'Presets & sequences',
      presetsD: 'Save reusable emoji presets and apply a repeating sequence across many stickers in visual order — to a selection or the whole pack.',
    },
    publish: {
      title: 'Publishing',
      linkT: 'Choose your pack link',
      linkD: 'Pick a custom link prefix. Astral checks availability live and always appends the required “_by_AstralStickerBot” suffix.',
      progressT: 'Publish progress',
      progressD: 'Publishing runs in the background with real progress. For big packs you’ll see stickers upload one by one, then a success screen with the link.',
      syncT: 'Update a published pack',
      syncD: 'Edit a published project and tap “Update Telegram Pack” to sync additions, removals, emoji and order — without creating a duplicate. New packs are Astral-managed and branded ﹙@AstralStickerBot﹚.',
    },
    premium: {
      title: 'Premium',
      freeT: 'Free limits',
      freeD: 'Free plans allow a set number of packs per month by type. Owners and admins have no limits.',
      howT: 'How to upgrade',
      howD: 'Open Premium, pick a duration, then choose a payment method. Everything is completed securely in the Astral bot.',
      methodsT: 'Toman or Stars',
      methodsD: 'Pay in Toman by sending a receipt for admin review, or pay instantly with Telegram Stars — Stars activates Premium automatically.',
    },
    more: {
      title: 'More',
      channelsT: 'Astral Channels',
      channelsD: 'A curated list of Astral’s official Telegram channels. Tap one to open it. Only admins add or edit entries — it’s optional and never affects your packs.',
      appearanceT: 'Language & appearance',
      appearanceD: 'Switch between English, فارسی and Русский, and choose a theme in Settings → Appearance.',
      helpT: 'Need help?',
      helpD: 'Reach the Astral team through the official channel linked in Astral Channels.',
    },
  },
  home: {
    greeting: 'Hello, {name}',
    heroKicker: 'Astral Studio',
    heroTitle: 'Sticker Studio',
    heroSubtitle: 'Create beautiful Telegram sticker packs.',
    createPack: 'Create New Pack',
    createPackSub: 'Start here',
    myProjects: 'My Projects',
    settings: 'Settings',
    adminPanel: 'Admin Panel',
    quickActions: 'Quick actions',
    recentProjects: 'Recent projects',
    viewAll: 'View all',
    noRecent: 'No projects yet. Create your first pack!',
    toolsTitle: 'Astral Tools',
    tools: {
      projects: 'Projects',
      projectsSub: 'Your sticker packs',
      premium: 'Premium',
      premiumSub: 'Unlock more',
      emojis: 'Emojis',
      emojisSub: 'Emoji presets',
      channels: 'Astral Channels',
      channelsSub: 'Official channels',
      guide: 'Guide',
      guideSub: 'Learn Astral',
      settings: 'Settings',
      settingsSub: 'Language & Support',
      admin: 'Admin',
      adminSub: 'Manage members',
    },
    officialName: '@AstralAtelier',
    officialSub: 'Follow us on Telegram',
    introTitle: 'Welcome to Astral ✦',
    introBody: 'Your studio for crafting Telegram sticker packs.',
    introCta: 'Enter the studio',
  },
  projects: {
    title: 'Projects',
    newProject: 'New project',
    searchPlaceholder: 'Search projects…',
    empty: 'No projects yet',
    emptyHint: 'Create your first project to get started.',
    emptySearch: 'No projects match your search.',
    emptyArchived: 'No archived projects.',
    drafts: 'Drafts',
    all: 'All',
    lastEdited: 'Edited {date}',
    autosavedAt: 'Autosaved {date}',
    open: 'Open',
    continueEditing: 'Continue',
    overview: 'Overview',
    category: 'Category',
    allCategories: 'All categories',
    stickerCount: '{count} stickers',
    more: 'More',
    archive: 'Archive',
    restore: 'Restore',
    deleteConfirmTitle: 'Delete project',
    deleteConfirmBody: 'Delete “{title}”? This cannot be undone.',
    archiveConfirmTitle: 'Archive project',
    archiveConfirmBody: 'Archive “{title}”? You can restore it anytime.',
    restoreConfirmTitle: 'Restore project',
    restoreConfirmBody: 'Restore “{title}” to your active projects?',
    status: {
      DRAFT: 'Draft',
      ACTIVE: 'Active',
      ARCHIVED: 'Archived',
    },
  },
  projectForm: {
    createTitle: 'New project',
    editTitle: 'Edit project',
    titleLabel: 'Title',
    titlePlaceholder: 'My awesome pack',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'What is this pack about?',
    statusLabel: 'Status',
    categoryLabel: 'Category',
    typeLabel: 'Sticker type',
    tagsLabel: 'Tags',
    tagsPlaceholder: 'Add a tag and press Enter',
    notesLabel: 'Notes',
    notesPlaceholder: 'Jot down ideas for this pack…',
    create: 'Create project',
    save: 'Save changes',
    historyTitle: 'Version history',
    history: 'History',
    noHistory: 'No history yet. Your edits are saved automatically.',
    restore: 'Restore',
  },
  stickers: {
    failure: {
      unreadable: 'We couldn’t read this image.',
      tooLarge: 'This file is too large.',
      tooDetailed: 'This image is too detailed to fit a sticker.',
      temporary: 'We couldn’t process this image. Try again.',
      retrying: 'Processing failed temporarily. Retrying…',
      cancelled: 'Upload cancelled.',
    },
    title: 'Stickers',
    count: '{count} stickers',
    add: 'Add sticker',
    empty: 'No stickers yet',
    emptyHint: 'Add your first sticker to build the pack.',
    remove: 'Remove sticker',
    removeConfirmTitle: 'Remove sticker',
    removeConfirmBody: 'Remove this sticker from the pack?',
    moveUp: 'Move up',
    moveDown: 'Move down',
    added: 'Sticker added',
    removed: 'Sticker removed',
    replaced: 'Sticker replaced',
    addHint: 'Auto-converted to a Telegram sticker.',
    processing: 'Processing…',
    processFailed: 'Could not process that file.',
    limitReached: 'Sticker limit reached for this pack.',
    errorReadFailed: 'Could not read that file.',
    emoji: 'Emoji',
    changeEmoji: 'Change emoji',
    emojiListLabel: 'Emojis (1–20)',
    emojiUpdated: 'Emoji updated',
    replace: 'Replace',
    preview: 'Preview',
    select: 'Select',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    range: 'Range',
    rangeArmed: 'Tap end…',
    applyEmoji: 'Apply emoji',
    editEmoji: 'Edit emoji',
    applySequence: 'Apply sequence',
    selectedCount: '{count} selected',
    bulkTitle: 'Emoji for {count}',
    bulkSame: 'Same',
    bulkSameDesc: 'Apply one emoji to every selected sticker.',
    bulkSequence: 'Sequence',
    bulkSequenceDesc: 'Repeat an emoji sequence across the selected stickers.',
    bulkPreset: 'Preset',
    bulkPresetDesc: 'Apply a saved preset across the selected stickers.',
  },
  emoji: {
    title: 'Choose emoji',
    searchPlaceholder: 'Search emoji…',
    noResults: 'No emoji found.',
    useKeyboard: 'Use device keyboard',
    customPlaceholder: 'Type or paste any emoji…',
    addEmoji: 'Add',
    invalid: 'Not an emoji',
    categories: {
      recent: 'Recent',
      smileys: 'Smileys',
      gestures: 'Gestures',
      people: 'People',
      animals: 'Animals',
      food: 'Food',
      activity: 'Activity',
      travel: 'Travel',
      objects: 'Objects',
      hearts: 'Hearts',
      symbols: 'Symbols',
      flags: 'Flags',
    },
  },

  presets: {
    title: 'Emoji presets',
    create: 'New preset',
    edit: 'Edit',
    name: 'Preset name',
    namePlaceholder: 'e.g. Cute',
    sequenceLabel: 'Emoji sequence',
    empty: 'No presets yet.',
    save: 'Save preset',
    saveAs: 'Save as preset',
    created: 'Preset created',
    updated: 'Preset updated',
    deleted: 'Preset deleted',
    saveFailed: 'Could not save preset.',
    apply: 'Apply',
    manage: 'Manage presets',
    reorderHint: 'Hold & drag to reorder',
    add: 'Add emoji',
    defaultLabel: 'Default emoji',
    singleDefault: 'Single default',
    autoApply: 'Auto-apply to new stickers',
    applyToAll: 'Apply to all stickers',
  },
  batch: {
    progress: '{done}/{total} processed',
    added: '{count} added',
    failed: '{count} failed',
    retry: 'Retry failed ({count})',
    capacity: 'Added {added} — pack limit reached.',
  },
  stickerTypes: {
    IMAGE: 'Image',
    VIDEO: 'Video',
    MIXED: 'Mixed',
  },
  membership: {
    title: 'Join Astral first ✦',
    body: 'Join {channel} to use Sticker Studio.',
    join: 'Join channel',
    checkAgain: "I've joined — Check again",
    unavailable: 'We couldn’t check your membership just now. Please try again in a moment.',
    required: 'Join {channel} to create and publish packs.',
  },
  publish: {
    error: {
      syncInProgress: 'This pack is already being updated. Give it a moment.',
      packFull: 'This pack is full. Telegram allows up to 120 stickers — remove a few and update again.',
      packMissing: 'This pack no longer exists on Telegram.',
      stickerRejected: 'Telegram rejected one of the stickers. Try replacing it.',
      mappingUnresolved: 'We couldn’t match this project to its Telegram pack, so nothing was changed. Please contact support.',
      rateLimited: 'Telegram is busy right now. Please try again in a moment — your changes are saved.',
      telegramUnavailable: 'Telegram is temporarily unavailable. Please try again.',
      mediaMissing: 'A sticker’s media could not be read. Try replacing that sticker.',
      unknown: 'Something went wrong while updating the pack. Please try again.',
    },
    action: 'Create Sticker Pack',
    publishing: 'Creating pack…',
    successTitle: 'Pack created',
    open: 'Open in Telegram',
    openPack: 'Open pack',
    copyLink: 'Copy link',
    needStickers: 'Add at least one sticker first.',
    failed: 'Could not create the pack.',
    published: 'Published',
    count: '{count} stickers',
    update: 'Update Telegram Pack',
    updating: 'Updating…',
    synced: 'Synced',
    localChanges: 'Changes not published',
    syncFailed: 'Sync failed',
    syncDone: 'Pack updated',
    packMissing: 'Pack not found on Telegram',
    reviewTitle: 'Review pack',
    reviewCta: 'Review & publish',
    progressTitle: 'Publishing',
    ready: 'Sticker pack ready ✨',
    retry: 'Retry',
    failedAt: 'Failed during {phase}',
    phase: {
      PREPARING: 'Preparing files',
      CREATING_SET: 'Creating sticker set',
      UPLOADING: 'Uploading stickers',
      FINALIZING: 'Finalizing',
    },
  },

  packName: {
    action: 'Edit pack name',
    title: 'Edit pack name',
    nameLabel: 'Pack name',
    currentLabel: 'Current title on Telegram',
    newLabel: 'New title on Telegram',
    trimmed: 'Shortened to fit Telegram’s 64-character limit.',
    hint: 'The name is updated on Telegram straight away. Your pack link never changes.',
    saved: 'Pack name updated',
    failed: 'Could not update the pack name.',
  },
  packLink: {
    title: 'Sticker pack link',
    nameLabel: 'Pack name',
    branding: {
      label: 'Pack branding',
      none: 'No tag',
      start: 'Tag first',
      end: 'Tag last',
    },
    linkLabel: 'Pack link',
    linkPlaceholder: 'cute_cats',
    checking: 'Checking availability…',
    available: 'Available',
    taken: 'Already taken',
    error: {
      empty: 'Enter a link',
      start: 'Must start with a letter',
      chars: 'Only letters, digits and underscores',
      underscore: 'No consecutive or trailing underscores',
      tooLong: 'Too long',
    },
  },
  channels: {
    title: 'Astral Channels',
    subtitle: "Astral's official channels.",
    empty: 'No channels yet.',
    add: 'Add channel',
    addTitle: 'Add channel',
    editTitle: 'Edit channel',
    usernameLabel: 'Channel username',
    usernamePlaceholder: '@channel',
    nameLabel: 'Name',
    labelLabel: 'Label',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Short description',
    categoryLabel: 'Category',
    open: 'Open in Telegram',
    manage: 'Manage',
    removeConfirmTitle: 'Remove channel',
    removeConfirmBody: 'Remove {name} from Astral Channels?',
  },
  usage: {
    title: 'Usage',
    plan: 'Plan',
    free: 'Free',
    premium: 'Premium',
    packs: '{used}/{limit} packs',
    unlimited: 'Unlimited',
    remaining: '{count} left',
    limitTitle: 'Monthly limit reached',
    limitBody: "You've used all your free {type} packs this month.",
    upgrade: 'Upgrade to Premium',
    premiumSoon: 'Premium is coming soon.',
  },
  categoryManager: {
    add: 'Add category',
    manage: 'Manage categories',
    name: 'Category name',
    create: 'Create',
    delete: 'Delete category',
    deleteConfirm: 'Delete this category?',
    deleteBody: 'Projects using it will become uncategorized.',
    empty: 'No categories yet.',
    none: 'No category',
    created: 'Category added',
    deleted: 'Category deleted',
    errorRequired: 'Category name is required.',
    errorDuplicate: 'This category already exists.',
    errorReserved: 'This category already exists.',
    errorTooLong: 'That name is too long.',
    errorFailed: 'Could not save the category.',
    legacy: 'No category',
  },
  categories: {
    characters: 'Characters',
    movies: 'Movies',
    shows: 'Shows',
    comics: 'Comics',
    games: 'Games',
    memes: 'Memes',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    theme: 'Theme',
    colorTheme: 'Color theme',
    themeMode: 'Theme mode',
    language: 'Language',
    profile: 'Profile',
    account: 'Account',
    notifications: 'Notifications',
    notificationsDesc: 'Receive updates about your projects.',
    role: 'Role',
    about: 'About',
    version: 'Version',
    themeModes: {
      dark: 'Dark',
      light: 'Light',
      auto: 'Auto',
      custom: 'Custom',
    },
  },
  admin: {
    title: 'Admin Panel',
    subtitle: 'Manage your team and members.',
    tabUsers: 'Users',
    tabAdmins: 'Admins',
    searchPlaceholder: 'Search users…',
    makeAdmin: 'Make admin',
    removeAdmin: 'Remove admin',
    you: 'You',
    noUsers: 'No users found.',
    memberSince: 'Member since {date}',
    total: '{count} members',
    details: 'User details',
    telegramId: 'Telegram ID',
    viewProfile: 'View Telegram profile',
    noProfile: 'No public username',
    premium: 'Premium',
    grantPremium: 'Grant Premium',
    revokePremium: 'Revoke Premium',
    copyId: 'Copy ID',
    premiumUntil: 'Premium until {date}',
    filterAll: 'All',
    filterAttention: 'Needs attention',
    filterPremium: 'Premium',
    filterPayments: 'Payments',
    filterAdmins: 'Admins',
    attentionReceipt: 'Receipt to review',
    reviewTitle: 'Payment review',
    reviewOpen: 'Open review',
    queueEmpty: 'Nothing to review right now.',
    plan: 'Plan',
    price: 'Price',
    requested: 'Requested',
    receipt: 'Receipt',
    viewReceipt: 'View receipt',
    noReceipt: 'No receipt yet',
    approve: 'Approve',
    reject: 'Reject',
    approveConfirm: 'Approve this payment and activate Premium?',
    rejectConfirm: 'Reject this payment? Premium will not be activated.',
    rejectReason: 'Reason (optional)',
    approved: 'Approved',
    rejected: 'Rejected',
    stAWAITING_RECEIPT: 'Awaiting receipt',
    stRECEIPT_SUBMITTED: 'Needs review',
    stAPPROVED: 'Approved',
    stREJECTED: 'Rejected',
    stEXPIRED: 'Expired',
    stCANCELLED: 'Cancelled',
    stAWAITING_PAYMENT: 'Awaiting payment',
    stCOMPLETED: 'Completed',
    stREFUNDED: 'Refunded',
    stFAILED: 'Failed',
    actionDone: 'Done',
    method: 'Method',
    methodToman: 'Toman / Manual',
    methodStars: 'Stars / Automatic',
    starsAmount: '⭐ {amount}',
    chargeId: 'Charge ID',
    filterHistory: 'History',
  },
  roles: {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    USER: 'User',
  },
  toast: {
    created: 'Project created',
    archived: 'Project archived',
    restored: 'Project restored',
    duplicated: 'Project duplicated',
    deleted: 'Project deleted',
  },
  auth: {
    signingIn: 'Signing you in…',
    notInTelegram: 'Open in Telegram',
    notInTelegramHint: 'This app runs inside Telegram. Please open it from the bot.',
    failed: 'Sign-in failed',
    retry: 'Try again',
  },
  errors: {
    generic: 'Something went wrong.',
    network: 'Network error. Check your connection.',
    unauthorized: 'Your session expired. Please sign in again.',
    notFound: 'Not found.',
    loadFailed: 'Failed to load. Please try again.',
  },

  premium: {
    title: 'Astral Premium',
    active: 'Active',
    free: 'Free plan',
    statusFree: "You're on the Free plan",
    statusActive: 'Astral Premium is active',
    expires: 'Expires {date}',
    daysLeft: '{count} days remaining',
    homeTitle: 'Astral Premium',
    homeBody: 'More packs and higher limits.',
    view: 'View Premium',
    renew: 'Renew / Extend',
    upgradeHint: 'You can create more packs and use higher limits with Astral Premium.',
    unlimitedPacks: 'Unlimited monthly packs',
    maxCapacityShort: 'Max capacity',
    tagIncluded: 'Astral tag: ﹙@AstralStickerBot﹚',
    tagNone: 'No Astral tag',
    starsUnit: 'Stars',
    perPackTotal: '{count} total / pack',
    mixedNote: 'images + videos combined',
    compareTitle: 'What you get',
    planFree: 'Free',
    planMonth1: '1 month',
    planMonth3: '3 months',
    perPack: '{count} / pack',
    maxCapacity: 'Max supported capacity',
    subscribe: 'Subscribe · {price}',
    tomanAmount: '{amount} Toman',
    imagePacks: 'Image packs',
    videoPacks: 'Video packs',
    mixedPacks: 'Mixed packs',
    capacity: 'Stickers per pack',
    plansTitle: 'Choose a plan',
    bestValue: 'Best value',
    save: 'Save {percent}%',
    month1: '1 month',
    month3: '3 months',
    month12: '12 months',
    perksTitle: 'What Premium unlocks',
    perkPacks: 'Unlimited sticker packs every month',
    perkStickers: 'Up to 120 stickers per pack, every type',
    freeLimitsTitle: 'Your Free limits',
    premiumLimitsTitle: 'With Premium',
    unlimited: 'Unlimited',
    packsPerMonth: '{count} packs / month',
    continueInBot: 'Continue in Telegram',
    purchaseNote: 'Purchases are completed securely in the Astral bot.',
    methodTitle: 'Payment method',
    methodToman: 'Pay in Toman',
    methodTomanSub: 'Manual · receipt verification',
    methodStars: 'Telegram Stars',
    methodStarsSub: 'Instant · automatic activation',
    toman: 'Toman',
    payStars: 'Pay with ⭐ {amount}',
    continueStars: 'Continue to pay with Stars',
    selectPlan: 'Select a plan',
    pendingTitle: 'Payment in review',
    pendingBody: 'Your receipt is being reviewed. Premium will activate after approval.',
    awaitingTitle: 'Finish in the bot',
    awaitingBody: 'Open the bot to submit your payment receipt.',
    loadFailed: 'Could not load Premium info.',
  },

  premiumNotify: {
    receiptReceived: 'Receipt received ✅ An Astral admin will review it. Premium will activate after approval.',
    approvedTitle: '✨ Astral Premium is now active!',
    approvedBody: 'Your {months}-month Premium is active. It expires on {date}.',
    openApp: 'Open Astral Studio',
    rejectedTitle: 'Receipt not approved',
    rejectedBody: 'Your payment receipt was not approved, so Premium was not activated.',
    rejectedReason: 'Reason: {reason}',
    tryAgain: 'Try again',
  },

  premiumBot: {
    intro: '✨ Astral Premium unlocks unlimited sticker packs and higher limits. Choose a plan:',
    chosen: 'Astral Premium — {plan}',
    chooseMethod: 'Choose a payment method:',
    methodToman: 'Toman · {amount}',
    methodStars: 'Telegram Stars · ⭐ {amount}',
    toman: 'Toman',
    invoiceTitle: 'Astral Premium — {plan}',
    invoiceDesc: 'Astral Premium subscription ({plan}). Instant automatic activation.',
    invoiceFailed: 'Could not start the Stars payment. Please try again with /premium.',
    planPick: 'Astral Premium — {plan} for {price} {currency}.',
    windowNote:
      'When you are ready to pay, press the button below. After starting the payment session, you have {minutes} minutes to submit your receipt. If it expires, simply start a new request.',
    ready: "I'm ready to pay",
    back: 'Back',
    plansAgain: 'See plans',
    sessionTitle: '💳 Payment session started — {plan} ({price} {currency})',
    paymentTitle: 'Payment details:',
    cardNumber: 'Card number: {value}',
    cardHolder: 'Name: {value}',
    bankName: 'Bank: {value}',
    tomanUnavailable:
      'Card payment is temporarily unavailable. You can pay with Telegram Stars instead, or try again later.',
    expiresAt: 'You have {minutes} minutes to pay and send your receipt.',
    submit: 'Submit receipt',
    cancel: 'Cancel',
    sendReceipt: 'Please send your payment receipt now as a photo or an image/PDF document.',
    receiptConfirmed: 'Receipt received ✅ An admin will review it shortly. Premium activates after approval.',
    cancelled: 'Payment request cancelled. You can start again anytime with /premium.',
    expired: 'This payment session has expired. Start a new one with /premium.',
    noActive: "You don't have an active payment session. Start one with /premium.",
    notImage: 'Please send the receipt as a photo or an image/PDF document.',
    open: 'Open Astral Studio',
  },
  bot: {
    welcomeTitle: '✦ Astral Studio',
    welcomeBody: 'Welcome to Sticker Studio — turn your images and videos into Telegram sticker packs.',
    startHint: 'To get started, tap “Open App”.',
    chooseLanguage: 'Choose your bot language',
    menuButton: 'Open App',
    premiumInfo:
      '✦ Astral Premium\n\n' +
      'Choose your plan and payment method in the Mini App.\n' +
      "You'll come back to this bot to complete the payment:\n\n" +
      '⭐ Stars — pay right here in the bot\n' +
      '💳 Toman — get the card details here and send your receipt\n\n' +
      'Open the Mini App and go to Premium to get started.',
    help: {
      title: '✦ Astral Sticker Studio — Help',
      botTitle: '🤖 In this bot',
      botBody:
        '/start — choose your language and open the app\n' +
        '/premium — how Astral Premium works\n' +
        '/help — this guide\n' +
        'The menu button next to the message box opens Sticker Studio.',
      payTitle: '⭐ Payment happens here, in the bot',
      payBody:
        'Pick your plan in the Mini App, then come back:\n' +
        '• Stars — pay the invoice the bot sends you\n' +
        '• Toman — the bot sends the card details; send your receipt back as a photo',
      appTitle: '✨ In the Mini App',
      appBody:
        '• New pack — choose Image, Video or Mixed\n' +
        '• Projects — all your packs, with search and filters\n' +
        '• Add stickers — upload images or videos; they are converted automatically\n' +
        '• Reorder — press and hold a sticker, then drag it\n' +
        '• Emoji — tap a sticker to set its emoji, or use Select to change several at once\n' +
        '• Publish — pick your pack link (t.me/addstickers/…) and create the pack\n' +
        '• Edit pack name — rename a published pack at any time\n' +
        '• Pack tag — Premium, Admin and Owner can move or remove the ﹙@AstralStickerBot﹚ tag\n' +
        '• Update pack — after editing a published pack, send the changes to Telegram\n' +
        '• Premium, Settings, Astral Channels and Guide are on the Home screen',
      staffTitle: '🛡 For admins',
      staffBody:
        'Open Admin from the Home screen to review Premium receipts and manage users.',
      channel: '📣 @AstralAtelier — news and new packs',
    },
  },
} as const;
